import React from 'react';
import { ActiveCustomer, AgentCustomerAnalysis } from '../../types/organizations';
import { getCategoryStyling } from '../../utils/categoryStyling';
import { getLatestAnalysis } from '../../utils/analysis';

interface CustomerAnalysisCardProps {
  customer: ActiveCustomer;
  onViewReasonings: (analysis: AgentCustomerAnalysis) => void;
}

// use shared getLatestAnalysis from utils/analysis


const CustomerAnalysisCard: React.FC<CustomerAnalysisCardProps> = ({ customer, onViewReasonings }) => {
  // Get latest analyses
  const customerInsight = getLatestAnalysis(customer.agent_analyses, 'customer_insight');
  const paymentReliability = getLatestAnalysis(customer.agent_analyses, 'payment_reliability_score');
  const financialRisk = getLatestAnalysis(customer.agent_analyses, 'financial_risk_score');
  const recommendations = getLatestAnalysis(customer.agent_analyses, 'recommended_actions');

  return (
    <div className="space-y-6">

        {/* Customer Insight Category */}
        {customerInsight && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Customer Category</h3>
              {customerInsight.output_json?.selected_category && (
                <div className={`px-4 py-2 ${getCategoryStyling(customerInsight.output_json.selected_category).bg} ${getCategoryStyling(customerInsight.output_json.selected_category).text} text-lg font-bold rounded-full`}>
                  {customerInsight.output_json.selected_category.replace(/customer/gi, '').trim()}
                </div>
              )}
            </div>
            {customerInsight.output_json?.customer_profile_summary && (
              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded mb-3">
                {customerInsight.output_json.customer_profile_summary}
              </div>
            )}
            <div className="flex justify-start">
              <button
                onClick={() => onViewReasonings(customerInsight)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
              >
                View Reasonings
              </button>
            </div>
          </div>
        )}

        {/* Risk Assessments - Side by Side */}
        {(paymentReliability || financialRisk) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Reliability Score */}
            {paymentReliability && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-lg font-semibold text-gray-900">Payment Risk</h4>
                  <div className="text-2xl font-bold text-orange-900">
                    {typeof paymentReliability.output_json?.score === 'string' 
                      ? parseInt(paymentReliability.output_json.score) || 0 
                      : paymentReliability.output_json?.score || 0}/10
                  </div>
                </div>
                
                <div className="flex justify-start">
                  <button
                    onClick={() => onViewReasonings(paymentReliability)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                  >
                    View Reasonings
                  </button>
                </div>
              </div>
            )}

            {/* Financial Risk Score */}
            {financialRisk && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-lg font-semibold text-gray-900">Financial Risk </h4>
                  <div className="text-2xl font-bold text-red-900">
                    {typeof financialRisk.output_json?.score === 'string' 
                      ? parseInt(financialRisk.output_json.score) || 0 
                      : financialRisk.output_json?.score || 0}/10
                  </div>
                </div>
                
                <div className="flex justify-start">
                  <button
                    onClick={() => onViewReasonings(financialRisk)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                  >
                    View Reasonings
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {recommendations && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
            {recommendations.output_json?.actions && (
              <div className="space-y-2">
                {recommendations.output_json.actions.map((action: string, index: number) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <span className="text-gray-600 text-sm font-medium">{index + 1}.</span>
                    </div>
                    <p className="text-sm text-gray-700">{action}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No Analysis Available */}
        {!customerInsight && !paymentReliability && !financialRisk && !recommendations && (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-gray-500 text-lg mb-2">No Analysis Available</div>
            <div className="text-gray-400 text-sm">Run customer analysis to see insights and risk assessments.</div>
          </div>
        )}
    </div>
  );
};

export default CustomerAnalysisCard;
